import type { ObjectId } from '@racemap/sdk/schema/base';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { EventTypes } from '@racemap/utilities/consts/events';
import { isDefined } from '@racemap/utilities/functions/validation';
import { produce } from 'immer';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import type { State } from '../../reducers';
import type { EventsActions } from '../events_reducers';
import { logAndToast, withSavingState } from './helpers';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const createChildEventActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<EventsActions, 'addStage' | 'addContests' | 'removeContests' | 'swapChildEvents'> => ({
  addStage: (stageGroupId: ObjectId) =>
    withSavingState(
      set,
      async () => {
        const newStage = await apiClient.addStageToStageGroup(stageGroupId);
        await get().events.actions.loadChildEvents(stageGroupId.toHexString());
        return newStage;
      },
      logAndToast('Failed to add a stage', 'The attempt to add stage to stage group.'),
    ),
  addContests: (eventIdsToAdd: Array<ObjectId>, contestGroupId: ObjectId) =>
    withSavingState(
      set,
      async () => {
        const addedEventIds = await apiClient.addContestsToContestGroup(
          contestGroupId,
          eventIdsToAdd,
        );
        await get().events.actions.loadChildEvents(contestGroupId.toHexString());
        return addedEventIds;
      },
      (error) => {
        logAndToast(
          'Failed to add a contest',
          'The attempt to add contest to contest group.',
        )(error);
        return [];
      },
    ),
  removeContests: (eventIdsToRemove: Array<ObjectId>, contestGroupId: ObjectId) => {
    const oldEvents = eventIdsToRemove.map((id) => get().events.items.get(id.toHexString()));
    return withSavingState(
      set,
      async () => {
        set(
          produce((s) => {
            // optimistic update
            eventIdsToRemove.forEach((eventId) => {
              const removeCandidat = s.events.items.get(eventId.toHexString());
              if (removeCandidat == null) return;

              removeCandidat.parent = null;
              removeCandidat.index = null;
              removeCandidat.type = EventTypes.REGULAR;
            });
          }),
        );
        const removedEventIds = await apiClient.removeContestsFromContestGroup(
          contestGroupId,
          eventIdsToRemove,
        );

        await Promise.all(
          eventIdsToRemove.map((eventId) => get().events.actions.loadEvent(eventId.toHexString())),
        );
        return removedEventIds;
      },
      (error) => {
        set(
          produce((s) => {
            for (const event of oldEvents) {
              if (isDefined(event)) {
                s.events.items.set(event.id, event);
              }
            }
          }),
        );
        logAndToast('Failed to add a event', 'The attempt to add the event failed.')(error);
        return [];
      },
    );
  },
  swapChildEvents: async (e1, e2) => {
    try {
      set(
        produce((s) => {
          // optimistic update
          s.events.items.get(e1.id).index = e2.index;
          s.events.items.get(e2.id).index = e1.index;
        }),
      );
      await get().events.actions.updateEvent(e1.id, [{ key: 'index', newValue: e2.index }]);
    } catch (error) {
      set(
        produce((s) => {
          s.events.items.get(e1.id).index = e1.index;
          s.events.items.get(e2.id).index = e2.index;
        }),
      );
      console.log(`Failed to swap events: ${error}`);
      toast.error('The attempt to swap the events failed.');
    }
  },
});
